import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

/** "Terms & Conditions" -> "terms-conditions" (mirrors the backend slugify). */
const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  // Once the admin edits the slug by hand we stop auto-deriving it from the title.
  const [slugTouched, setSlugTouched] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    status: true,
  });

  useEffect(() => {
    if (id) getSingleData();
  }, [id]);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/pages/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const page = data.data;
        setFormData({
          title: page.title || '',
          slug: page.slug || '',
          content: page.content || '',
          status: !!page.status,
        });
        setSlugTouched(true); // existing pages already have a fixed slug
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Keep the slug in sync with the title until the admin overrides it.
      if (name === 'title' && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setFormData((prev) => ({ ...prev, slug: e.target.value }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, content: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const cleanSlug = slugify(formData.slug || formData.title);
    if (!cleanSlug) {
      toast.error('Slug is required');
      return;
    }

    setIsLoader(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('slug', cleanSlug);
      data.append('content', formData.content || '');
      data.append('status', formData.status);

      const response = await ApiService.request({
        method: 'POST',
        url: `admin/pages/createOrUpdate/${id || ''}`,
        data,
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/admin/pages');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Page' : 'Add Page'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/pages" className="text-muted text-hover-primary">Pages</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. Terms & Conditions"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Slug</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleSlugChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. terms-conditions"
                    />
                    <div className="form-text">
                      The mobile app fetches this page by its slug. Keep the slugs
                      <code className="mx-1">terms-conditions</code>,
                      <code className="mx-1">privacy-policy</code> and
                      <code className="mx-1">contact-support</code> unchanged.
                    </div>
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="form-label">Content</label>
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleContentChange}
                      modules={QUILL_MODULES}
                      style={{ background: '#fff' }}
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        id="status"
                      />
                      <label className="form-check-label" htmlFor="status">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  <Link to="/admin/pages" className="btn btn-light">Cancel</Link>
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
